const DB_NAME = 'ai-interview-pending-audio';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
let databasePromise;

const openDatabase = () => {
  if (databasePromise) return databasePromise;
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      db.createObjectStore('turns', { keyPath: 'key' });
      db.createObjectStore('chunks', { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return databasePromise;
};

const transact = async (name, mode, operation) => {
  const db = await openDatabase();
  if (!db) throw new Error('Local audio recovery storage is unavailable');
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(name, mode);
    const request = operation(transaction.objectStore(name));
    let result;
    request.onsuccess = () => { result = request.result; };
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error('Audio storage transaction aborted'));
  });
};

export const voiceTurnKey = (sessionId, candidateId) => `${sessionId}:${candidateId}`;

export const savePendingTurn = (turn) =>
  transact('turns', 'readwrite', (store) => store.put({ ...turn, updatedAt: Date.now() }));

export const getPendingTurn = async (key) => {
  const saved = await transact('turns', 'readonly', (store) => store.getAll());
  for (const item of saved) {
    if (item.key.startsWith(`${key}:rejected:`) && Date.now() - item.updatedAt > MAX_AGE_MS) {
      await clearPendingTurn(item.key);
    }
  }
  const turn = await transact('turns', 'readonly', (store) => store.get(key));
  if (!turn) return null;
  if (Date.now() - turn.updatedAt <= MAX_AGE_MS) return turn;
  await clearPendingTurn(key);
  return null;
};

export const savePendingChunk = (key, seq, pcm) =>
  transact('chunks', 'readwrite', (store) => store.put({ id: `${key}:${seq}`, key, seq, pcm }));

export const getPendingChunk = (key, seq) =>
  transact('chunks', 'readonly', (store) => store.get(`${key}:${seq}`));

export const getPendingChunks = async (key) => {
  const all = await transact('chunks', 'readonly', (store) => store.getAll());
  return all.filter((item) => item.key === key).sort((a, b) => a.seq - b.seq);
};

export const removePendingChunk = (key, seq) =>
  transact('chunks', 'readwrite', (store) => store.delete(`${key}:${seq}`));

export const clearPendingTurn = async (key) => {
  const chunks = await getPendingChunks(key);
  await Promise.all(chunks.map((item) => removePendingChunk(key, item.seq)));
  await transact('turns', 'readwrite', (store) => store.delete(key));
};

export const archivePendingTurn = async (turn) => {
  const archiveKey = `${turn.key}:rejected:${turn.utteranceId}`;
  const chunks = await getPendingChunks(turn.key);
  for (const item of chunks) await savePendingChunk(archiveKey, item.seq, item.pcm);
  await savePendingTurn({ ...turn, key: archiveKey, sourceKey: turn.key });
  // Keep a small recovery marker across reloads until a replacement starts.
  await savePendingTurn({ ...turn, deliveryState: 'needs_repeat' });
  for (const item of chunks) await removePendingChunk(turn.key, item.seq);
};
